import {
    IEventAnalyticGroupedByBatchId,
    IFetchEventAnalyticGroupedByBatchIdRequestParams,
    IFetchEventAnalyticRequestParams,
} from '../../domain/event-analytic/interface';
import App from 'antd/lib/app';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePageLocation } from '../usePageState';
import { useDocumentTitle } from '../../components/useDocumentTitle';
import { PageHeader } from '../../components/PageHeader';
import Button from 'antd/lib/button';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import { useState, useMemo, useEffect, useContext } from 'react';
import {
    getColumnSortOrder,
    EventAnalyticsDataRangeFilterValues,
    EventAnalyticsDurationFilterValues,
    EventAnalyticNominationPresetFilterValues,
} from '../../components/table/columnFormatHelpers';
import { WithEmptyDataTable } from '../../components/table/withEmptyDataTable';
import { TablePaginationConfig } from 'antd/lib/table';
import { DEFAULT_PAGINATION } from '../../domain/commonConst';
import { IPaginationMeta } from '../../domain/IPagination';
import { useEventAnalyticGroupedByBatchIdListQuery } from '../../domain/event-analytic/queries';
import { toUsefulParams } from '../toUsefulParams';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { MemoizedDatetimePopover } from '../../components/datetimePopover/datetimePopover';
import {
    DownloadEventPerformanceCsvModal,
    IDownloadEventPerformanceCsvFormData,
} from './downloadEventPerformanceModal/DownloadEventPerformanceModal';
import { exportEventAnalyticGroupedByBatch, exportEventAnalytic } from '../../domain/event-analytic';
import { useAuth } from '../../domain/auth/useAuth';
import { useProgramListQuery } from '../../domain/program/queries';
import { IProgram } from '../../domain/program/interface';
import { getProgramNameFromSource } from '../../domain/event/getProgramNameFromSource';
import { isArray, isNumber, isEmpty } from 'lodash';
import './event-performance.css';
import CalendarOutlined from '@ant-design/icons/CalendarOutlined';
import { ActionList } from '../../components/table/actionList/actionList';
import { metricSorter } from '../../domain/event-analytic/metricSorter';
import { displayMetric } from '../../domain/event-analytic/displayMetric';
import Tooltip from 'antd/lib/tooltip';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import { SECONDARY_COLOR } from '../../theme';
import { AreaChartOutlined } from '@ant-design/icons';
import { PerformanceChartModal, IPerformanceChartModalConfig } from './performanceChartModal/PerformanceChartModal';
import { resolveBaselineMarketFromMarkets } from '../../domain/energy/baselineMarket';
import { CustomizableColumnType, CustomizableTable } from '../../components/table/CustomizableTable';
import { CustomizableTableContextProvider } from '../../components/table/CustomizableTableContext';
import Layout from 'antd/lib/layout';
import { PageFooter } from '../../components/PageFooter';
import { EventPerformanceSidebar } from './sidebar/EventPerformanceSidebar';
import { useEventPerformanceSidebar } from './sidebar/useEventPerformanceSidebar';
import { IEventPerformanceSidebarFiltersContext, EventPerformanceSidebarFiltersContext } from './sidebar/EventPerformanceSidebarFiltersContext';
import { SidebarGrid } from 'src/components/Sidebar/SidebarGrid';
import { ProgramTag } from '../../components/ProgramTag';

export interface IEventPerformancePageState {
    companyId?: number;
    filter?: {
        program_name?: number | number[];
        date_range_preset?: EventAnalyticsDataRangeFilterValues;
        event_duration_preset?: EventAnalyticsDurationFilterValues;
        nomination_preset?: EventAnalyticNominationPresetFilterValues;
    };
    pagination?: {
        pageSize: number;
        current: number;
    };
    sorter?: {
        field: string;
        order: 'ascend' | 'descend';
    };
}

export const EventPerformanceByBatchId = () => {
    const { notification } = App.useApp();
    const location = useLocation();
    const navigation = useNavigate();
    const { setPageQuery, queryToState } = usePageLocation();
    const pageState: IEventPerformancePageState = queryToState(location.search);
    const auth = useAuth()!;
    const isCustomerRoleType = auth.user?.isCustomer() || auth.user?.isOperator();
    const { data: programsData } = useProgramListQuery({});
    const programs = useMemo(() => programsData?.data || [], [programsData?.data]);

    const [csvDownloadPopupOpen, setCsvDownloadPopupOpen] = useState<boolean>(false);
    const [isExportLoading, setIsExportLoading] = useState(false);
    const [chartPopupConfig, setChartPopupConfig] = useState<IPerformanceChartModalConfig | null>(null);
    const [isDefaultFilterApplied, setIsDefaultFilterApplied] = useState<boolean>(false);

    useDocumentTitle('Estimated Event Performance');

    const { sidebarCollapsed, setSidebarCollapsed, layoutPaddingLeft } = useEventPerformanceSidebar();
    const { filters, handleDefaultFilters } = useContext<IEventPerformanceSidebarFiltersContext>(EventPerformanceSidebarFiltersContext);

    const listQuery = {
        pagination: pageState?.pagination ?? DEFAULT_PAGINATION,
        filter: {
            ...pageState?.filter,
        },
        ...(pageState?.sorter && { sorter: pageState?.sorter }),
        ...(pageState?.companyId && { companyId: pageState?.companyId }),
        programNames: getProgramNamesById(pageState?.filter?.program_name, programs),
    };

    const isAnyFilterSelected = !!pageState?.filter || !!pageState?.companyId;

    const { data, isFetching, isError, error } = useEventAnalyticGroupedByBatchIdListQuery(listQuery, {
        keepPreviousData: isAnyFilterSelected,
        enabled: isDefaultFilterApplied && isAnyFilterSelected,
    });

    const eventAnalyticGroups = data?.data || [];

    if (isError) {
        notification.error({ key: 'event-analytics-bath-fetch-error', message: error?.message || 'Cannot fetch event analytics!' });
    }

    const meta =
        data?.meta ??
        ({
            total: 0,
            offset: 0,
            limit: pageState && pageState.pagination && pageState.pagination.pageSize ? pageState.pagination.pageSize : DEFAULT_PAGINATION.pageSize,
        } as IPaginationMeta);

    // Apply default date range to avoid processing all items and reduce initial load time.
    // It still can be set by user
    useEffect(() => {
        if (!isEmpty(filters)) {
            setIsDefaultFilterApplied(true);
            return;
        }

        if (!isDefaultFilterApplied) {
            handleDefaultFilters({
                date_range_preset: 'current_quarter',
                nomination_preset: 'more_than_0',
            });
            setIsDefaultFilterApplied(true);
        }
    }, [filters, handleDefaultFilters, isDefaultFilterApplied]);

    const goToSitePerformance = (batchId: string) => {
        const companyId = pageState.companyId;
        const companyIdQuery = companyId ? `?companyId=${companyId}` : '';
        navigation(`/event-analytics/performance/${batchId}${companyIdQuery}`);
    };

    const onChartViewOpen = (record: IEventAnalyticGroupedByBatchId) => {
        const baselineMarket = resolveBaselineMarketFromMarkets(record.markets ?? []);
        setChartPopupConfig({
            batchId: record.event_batch,
            companyId: pageState.companyId,
            eventStartUtc: record.event_datetime_start,
            eventDurationMinutes: record.event_duration,
            nomination: record.nomination,
            averageBaseline: record.baseline,
            meter_count: record.meters_count,
            baselineMode: baselineMarket.mode,
            baselineWarning: baselineMarket.warning,
        });
    };

    async function onTableChange(
        pagination: TablePaginationConfig,
        _: Record<string, FilterValue | null>, // not used table filter
        sorter: SorterResult<IEventAnalyticGroupedByBatchId> | SorterResult<IEventAnalyticGroupedByBatchId>[]
    ) {
        const preparedParams = toUsefulParams({ pagination, sorter });

        setPageQuery({ ...pageState, ...preparedParams });
    }

    const onBatchCSVExport = async (options: IDownloadEventPerformanceCsvFormData) => {
        setCsvDownloadPopupOpen(false);
        try {
            setIsExportLoading(true);
            let params: IFetchEventAnalyticGroupedByBatchIdRequestParams = {};

            if (options.applyFilters) {
                params.filter = pageState.filter;
                params.sorter = pageState.sorter;
                params.companyId = pageState?.companyId;
                params.programNames = getProgramNamesById(pageState?.filter?.program_name, programs);
            }

            // Backend requires companyId for customer users even when filters are not applied
            if (isCustomerRoleType) {
                params.companyId = pageState?.companyId;
            }

            await exportEventAnalyticGroupedByBatch(params);
            setIsExportLoading(false);
        } catch (error) {
            notification.error({ key: 'event-analytics-csvdownload-error', message: 'Cannot download event analytics!' });
            setIsExportLoading(false);
        }
    };

    const onMeterLevelCSVExport = async (options: IDownloadEventPerformanceCsvFormData | null, batchId?: string) => {
        setCsvDownloadPopupOpen(false);
        try {
            setIsExportLoading(true);
            let params: IFetchEventAnalyticRequestParams = {};

            if (batchId) {
                params.batchId = batchId;
                params.sorter = pageState?.sorter;
                params.companyId = pageState?.companyId;
            } else if (options?.applyFilters) {
                params.sorter = pageState?.sorter;
                params.companyId = pageState?.companyId;
                params.filter = pageState.filter;
                params.programNames = getProgramNamesById(pageState?.filter?.program_name, programs);
            }

            // Backend requires companyId for customer users even when filters are not applied
            if (isCustomerRoleType) {
                params.companyId = pageState?.companyId;
            }

            await exportEventAnalytic(params);
            setIsExportLoading(false);
        } catch (error) {
            notification.error({ key: 'event-analytics-csvdownload-error', message: 'Cannot download event analytics!' });
            setIsExportLoading(false);
        }
    };

    const onCSVExport = async (options: IDownloadEventPerformanceCsvFormData) => {
        if (options.applyFilters && !eventAnalyticGroups.length) {
            notification.warning({
                key: 'no-data-for-csv-group-by-batch',
                message: 'No data for selection.',
            });
            return;
        }

        if (options.groupByBatch) {
            await onBatchCSVExport(options);
            return;
        }

        await onMeterLevelCSVExport(options);
    };

    const getActions = (item: IEventAnalyticGroupedByBatchId) => {
        return [
            ...(item.meters_count > 0
                ? [
                      {
                          title: () => 'Download CSV Details by Meter',
                          onClick: (record: IEventAnalyticGroupedByBatchId) => () => onMeterLevelCSVExport(null, record.event_batch),
                          disabled: isExportLoading,
                          icon: () => <DownloadOutlined />,
                      },
                  ]
                : []),
            ...(isNumber(item.baseline)
                ? [
                      {
                          title: () => 'Open Chart View',
                          onClick: (record: IEventAnalyticGroupedByBatchId) => () => onChartViewOpen(record),
                          icon: () => <AreaChartOutlined />,
                      },
                  ]
                : []),
            {
                title: () => 'Open Performance Details by Site',
                onClick: (record: IEventAnalyticGroupedByBatchId) => () => goToSitePerformance(record.event_batch),
                icon: () => <CalendarOutlined />,
            },
        ];
    };

    const eventAnalyticColumns: CustomizableColumnType<IEventAnalyticGroupedByBatchId>[] = [
        {
            title: 'Start Time',
            dataIndex: 'event_datetime_start',
            key: 'event_datetime_start',
            sorter: (a: any, b: any) => a.event_datetime_start - b.event_datetime_start,
            ...getColumnSortOrder('event_datetime_start', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedByBatchId) => {
                if (!record.event_datetime_start) return <></>;
                return <MemoizedDatetimePopover timestamp={new Date(record.event_datetime_start).valueOf()} timeZone={record.site_timezones!} />;
            },
        },
        {
            title: 'Duration',
            dataIndex: 'event_duration',
            key: 'event_duration',
            sorter: (a: any, b: any) => a.event_duration - b.event_duration,
            ...getColumnSortOrder('event_duration', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedByBatchId) => record.event_duration,
        },
        {
            title: 'Program',
            dataIndex: 'event_source',
            key: 'event_source',
            render: (text: string, record: IEventAnalyticGroupedByBatchId) => {
                const programName = getProgramNameFromSource(record.event_source);
                if (programName) return programName.split(',').map((name, i) => <ProgramTag programName={name} key={i} />);
                return <></>;
            },
        },
        {
            title: 'Sites',
            dataIndex: 'sites_count',
            key: 'sites_count',
            sorter: (a: any, b: any) => a.sites_count - b.sites_count,
            ...getColumnSortOrder('sites_count', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedByBatchId) => record.sites_count,
        },
        {
            title: 'Meters',
            dataIndex: 'meters_count',
            key: 'meters_count',
            sorter: (a: any, b: any) => a.meters_count - b.meters_count,
            ...getColumnSortOrder('meters_count', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedByBatchId) => record.meters_count,
        },
        {
            title: 'Nomination (kW)',
            dataIndex: 'nomination',
            key: 'nomination',
            sorter: (a, b) => metricSorter(a.nomination, b.nomination),
            ...getColumnSortOrder('nomination', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedByBatchId) => displayMetric(record.nomination),
        },
        {
            title: 'Baseline (kW)',
            dataIndex: 'baseline',
            key: 'baseline',
            sorter: (a, b) => metricSorter(a.baseline, b.baseline),
            ...getColumnSortOrder('baseline', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedByBatchId) => displayMetric(record.baseline),
        },
        {
            title: (
                <>
                    <span>Performance (kW)</span>{' '}
                    <Tooltip title="Relative to Baseline">
                        <QuestionCircleOutlined style={{ color: SECONDARY_COLOR }} />
                    </Tooltip>
                </>
            ),
            dataIndex: 'performance',
            key: 'performance',
            sorter: (a, b) => metricSorter(a.performance, b.performance),
            ...getColumnSortOrder('performance', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedByBatchId) => displayMetric(record.performance),
            showSorterTooltip: false,
        },
        {
            title: 'Performance (% of kW)',
            dataIndex: 'performance_percentage',
            key: 'performance_percentage',
            sorter: (a, b) => metricSorter(a.performance_percentage, b.performance_percentage),
            ...getColumnSortOrder('performance_percentage', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedByBatchId) => displayMetric(record.performance_percentage, true),
        },
        {
            title: 'Data (% of kW)',
            dataIndex: 'data_completion',
            key: 'data_completion',
            sorter: (a, b) => metricSorter(a.data_completion, b.data_completion),
            ...getColumnSortOrder('data_completion', pageState.sorter),
            render: (text: string, record: IEventAnalyticGroupedByBatchId) => displayMetric(record.data_completion, true),
        },
        {
            key: 'action',
            width: 120,
            sorter: false,
            render: (text: string, record: IEventAnalyticGroupedByBatchId) => <ActionList actions={getActions(record)} item={record} />,
        },
    ];

    return (
        <>
            <CustomizableTableContextProvider configKey="eventPerformance" columns={eventAnalyticColumns}>
                <Layout style={{ height: '100%', paddingLeft: layoutPaddingLeft }}>
                    <EventPerformanceSidebar
                        collapsed={sidebarCollapsed}
                        onCollapseChange={(collapsed: boolean) => setSidebarCollapsed(collapsed)}
                        totalRecords={meta?.total}
                        onFilterChange={() => {}}
                        gridComponent={<SidebarGrid />}
                    />

                    <Layout.Content style={{ padding: '0 26px' }}>
                        <PageHeader
                            pageTitle={`Estimated Event Performance`}
                            actions={[
                                <Button
                                    size="large"
                                    key="download-csv"
                                    onClick={() => setCsvDownloadPopupOpen(true)}
                                    loading={isExportLoading}
                                    icon={<DownloadOutlined />}
                                    data-cy="export-action"
                                >
                                    Download CSV
                                </Button>,
                            ]}
                        />
                        <WithEmptyDataTable
                            condition={isFetching}
                            emptyDataDescription={
                                !isAnyFilterSelected ? 'Please, select a company or at least one filter to display data' : undefined
                            }
                        >
                            <CustomizableTable<IEventAnalyticGroupedByBatchId>
                                size="small"
                                rowKey="event_batch"
                                className="event-performance-table"
                                sticky={{ offsetHeader: 62 }}
                                showHiddenColumnsCount={true}
                                dataSource={eventAnalyticGroups}
                                loading={isFetching}
                                onChange={onTableChange}
                                meta={meta}
                            />
                        </WithEmptyDataTable>

                        <PageFooter />
                    </Layout.Content>
                </Layout>
            </CustomizableTableContextProvider>
            {csvDownloadPopupOpen && (
                <DownloadEventPerformanceCsvModal allowGroupByBatch={true} onClose={() => setCsvDownloadPopupOpen(false)} onSubmit={onCSVExport} />
            )}
            {chartPopupConfig && <PerformanceChartModal config={chartPopupConfig} onClose={() => setChartPopupConfig(null)} />}
        </>
    );
};

function getProgramNamesById(activeProgramsId?: number | number[], programList: IProgram[] = []): string | null {
    if (!activeProgramsId || !programList.length) return null;

    const programIdsList: number[] = isArray(activeProgramsId) ? activeProgramsId : [activeProgramsId];
    const result: string[] = [];

    programIdsList?.forEach(programId => {
        const program = programList.find(program => program.program_id === programId);
        if (program?.name) {
            result.push(program.name);
        }
    });

    return result.join(',');
}
